/**
 * Recipe detail data layer.
 *
 * `useRecipeDetailQuery(id)` — detail-panel load; lazy via
 * `enabled: id != null`. Feeds the shared recipe detail slot used by
 * the /recipes make-list side peek (and the shopping-list surfaces).
 *
 * The Plan 08 browse layer (`useRecipesBrowseQuery` against
 * `GET /api/recipes`) was deleted with the universal catalog browser
 * in the Aug 2026 chef-sidebar split — only the detail types + fetch
 * and their pure helpers remain.
 */

import { useQuery } from '@tanstack/react-query'

import { apiFetch } from './http'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type RecipeComponentKind =
  | 'MAIN_ENTREE'
  | 'SIDE'
  | 'FRUIT'
  | 'VEGETABLE'
  | 'GRAIN'
  | 'BEVERAGE'
  | 'CONDIMENT'
  | 'SUPPLEMENT'

export type RecipeTagKind = 'DIETARY' | 'ALLERGEN' | 'FLAVOR' | 'MARKETING' | 'MENU_SLOT'

export interface RecipeTagRow {
  id: string
  tag: {
    id: string
    name: string
    slug: string
    kind: RecipeTagKind
    color: string | null
  }
}

export interface RecipeContainsTagRow extends RecipeTagRow {}

export interface RecipeDetailIngredientRow {
  id: string
  sortOrder: number
  sizeValue: number | null
  sizeUnit: string | null
  weightValue: number | null
  weightUnit: string | null
  volumeValue: number | null
  volumeUnit: string | null
  quantityValue: number | null
  quantityUnit: string | null
  /** RECIPE_YIELD (whole-batch amount) vs PER_SERVING — drives scaling. */
  quantityBasis: string | null
  /** Plan 28 non-linear scaling cap — shown as an advisory beside the scaled
   *  amount, never used to clamp it (the cap is "per batch/roasting pan" and
   *  the pan count is unknown here). */
  maxQuantityValue?: number | null
  maxQuantityUnit?: string | null
  scalingNotes?: string | null
  /** Resolved brand label for processed items (pinned → default → null). */
  brand: string | null
  ingredient: {
    id: string
    name: string
    description: string | null
    cnLabelNumber: string | null
    cnLabelStatement: string | null
    cnLabelSponsor: string | null
    containsTags: RecipeContainsTagRow[]
  }
}

export interface RecipeDetailInstructionRow {
  id: string
  body: string
  /** Nulls mean "no resolvable amount" — render the name only. */
  ingredientRefs: Array<{
    name: string
    quantity: number | null
    unit: string | null
    quantityBasis: string | null
  }>
}

export interface RecipeDetailHaccpPointRow {
  id: string
  kind: string
  title: string
  body: string | null
  criticalLimit: string | null
  correctiveAction: string | null
}

export interface RecipeDetailTipRow {
  id: string
  body: string
}

export interface RecipeDetailVariantRow {
  id: string
  name: string
  isDefault: boolean
  isArchived: boolean
  recipeId: string | null
  recipe: {
    id: string
    name: string | null
    prepTimeMinutes: number | null
    cookTimeMinutes: number | null
    yieldValue: number | null
    yieldUnit: string | null
    ingredients: RecipeDetailIngredientRow[]
    instructions: RecipeDetailInstructionRow[]
    haccpPoints: RecipeDetailHaccpPointRow[]
    tips: RecipeDetailTipRow[]
  } | null
}

export interface RecipeDetailComponent {
  id: string
  kind: RecipeComponentKind
  imageUri: string | null
  name: string
  description: string | null
  autoDescription: string
  wholeGrainRich: boolean
  vegetableSubgroup: string | null
  grainExhibitGroup: string | null
  tags: RecipeTagRow[]
  variants: RecipeDetailVariantRow[]
}

export interface RecipeDetailResponse {
  component: RecipeDetailComponent | null
}

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

export async function fetchRecipeDetail(id: string, signal?: AbortSignal): Promise<RecipeDetailResponse> {
  return apiFetch<RecipeDetailResponse>(`/api/recipes/${encodeURIComponent(id)}`, { signal })
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useRecipeDetailQuery(id: string | null) {
  return useQuery<RecipeDetailResponse>({
    queryKey: ['recipes', 'detail', id],
    queryFn: ({ signal }) => fetchRecipeDetail(id ?? '', signal),
    enabled: id !== null && id !== '',
    // Detail rarely changes per (id); cache aggressively across drawer opens.
    staleTime: 5 * 60 * 1000,
  })
}

// ---------------------------------------------------------------------------
// Pure helpers (kept compatible with the previous query module)
// ---------------------------------------------------------------------------

export const RECIPE_KIND_LABELS: Record<RecipeComponentKind, string> = {
  MAIN_ENTREE: 'Mains',
  SIDE: 'Sides',
  VEGETABLE: 'Vegetables',
  FRUIT: 'Fruits',
  GRAIN: 'Grains',
  BEVERAGE: 'Beverages',
  CONDIMENT: 'Condiments',
  SUPPLEMENT: 'Supplements',
}

/**
 * Pick the default ComponentVariant for a Component (or the first
 * non-archived one). Generic over the variant shape so any variant
 * row carrying the two flags flows through the same helper.
 */
export function pickDefaultRecipeVariant<V extends { isDefault: boolean; isArchived: boolean }>(
  variants: ReadonlyArray<V>,
): V | undefined {
  const live = variants.filter((v) => !v.isArchived)
  return live.find((v) => v.isDefault) ?? live[0]
}
